/**
 * ComingSoonInterest — WD-T4 §10 (WORKER-DASH-DECISIONS-2026-09-25 v1.2):
 * the Coming-Soon tiles must lead to INTERACTION that gauges demand, not a
 * dead label. Renders an 'I want this' button + the REAL count of interested
 * users ('N want this' — never an invented number). Loads counts on mount
 * only for COMING_SOON-tier users (PROJECT_MANAGER / ADMIN — same tier set
 * the server enforces). Idempotent: the server never double-counts a second
 * click, and the button flips to a disabled 'Requested'.
 */
import { useEffect, useState } from 'react';
import { featureApi, type FeatureInterestModule } from '@/utils/api';
import { canSeeTile } from '@/utils/roleGate';

export function ComingSoonInterest({ module: mod }: { module: FeatureInterestModule }) {
  const [count, setCount] = useState<number | null>(null);
  const [requested, setRequested] = useState(false);
  const [busy, setBusy] = useState(false);

  const currentRole = ((): string | undefined => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null')?.role as string | undefined;
    } catch {
      return undefined;
    }
  })();
  const gated = canSeeTile(currentRole, 'COMING_SOON');

  useEffect(() => {
    if (!gated) return undefined;
    let alive = true;
    featureApi.getCounts().then(({ counts, requested: mine }) => {
      if (!alive) return undefined;
      setCount(counts[mod] ?? 0);
      setRequested(mine.includes(mod));
    }).catch(() => {
      // Demand gauge is never load-bearing: a failed counts fetch just hides the number.
      if (alive) setCount(null);
    });
    return () => { alive = false; };
  }, [gated, mod]);

  if (!gated) return null;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // safe to live inside a tile <Link> (Rigging Calculator)
    e.stopPropagation();
    if (requested || busy) return;
    setBusy(true);
    try {
      const res = await featureApi.request(mod);
      setRequested(true);
      // Only bump on a REAL first registration — the server is the source of truth.
      if (!res.alreadyRequested) setCount((c) => (c === null ? null : c + 1));
    } catch {
      // leave state unchanged; user can click again
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 flex items-center gap-3" data-testid={`feature-interest-${mod}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={requested || busy}
        data-testid={`want-this-${mod}`}
        className={requested
          ? 'px-3 py-1.5 text-sm font-semibold rounded-lg bg-green-100 text-green-700 cursor-default'
          : 'px-3 py-1.5 text-sm font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition disabled:opacity-60'}
      >
        {requested ? 'Requested ✓' : busy ? 'Sending…' : 'I want this'}
      </button>
      {count !== null && (
        <span className="text-xs text-gray-500" data-testid={`want-count-${mod}`}>
          {count} want this
        </span>
      )}
    </div>
  );
}